import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useBlog } from '@/lib/blog/context';
import type { Article } from '@/lib/blog/types';
import SEO from '@/components/SEO';
import { getCanonicalUrl } from '@/utils/url';

interface BlogPostProps {
  slug: string;
}

export function BlogPost({ slug }: BlogPostProps) {
  const { getArticle, loading, error } = useBlog();
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    if (!slug) return;

    async function loadArticle() {
      const data = await getArticle(slug);
      setArticle(data);
    }

    loadArticle();
  }, [slug, getArticle]);

  // Redirect if no slug is provided
  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-red-600 mb-2">Error Loading Blog Post</h2>
        <p className="text-gray-600">{error.message}</p>
        <Link to="/blog" className="mt-4 inline-block text-purple-600 hover:text-purple-700">
          Return to Blog
        </Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Post Not Found</h2>
        <p className="text-gray-600">The blog post you're looking for doesn't exist.</p>
        <Link to="/blog" className="mt-4 inline-block text-purple-600 hover:text-purple-700">
          Return to Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${article.headline} | Reiki Near Me Blog`}
        description={article.metaDescription}
        canonicalUrl={getCanonicalUrl(`/blog/${article.slug}`)}
        ogType="article"
        ogImage={article.image}
      />
      <article className="max-w-4xl mx-auto px-4 py-8">
        <header className="mb-8">
          <nav className="mb-8">
            <Link to="/blog" className="text-purple-600 hover:text-purple-700">
              ← Back to Blog
            </Link>
          </nav>

          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{article.headline}</h1>

          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <time dateTime={article.createdAt}>
              {format(new Date(article.createdAt), 'MMMM d, yyyy')}
            </time>
            {article.readingTime && <span>{article.readingTime} min read</span>}
          </div>

          {article.category && (
            <div className="mt-4">
              <Link
                to={`/blog/category/${article.category.slug}`}
                className="text-purple-600 hover:text-purple-700"
              >
                {article.category.title}
              </Link>
            </div>
          )}

          {article.image && (
            <div className="mt-8">
              <img
                src={article.image}
                alt={article.headline}
                className="w-full h-[400px] object-cover rounded-lg"
              />
            </div>
          )}
        </header>

        <div
          className="prose prose-lg dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: article.html }}
        />

        <footer className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <Link
                key={tag.slug}
                to={`/blog/tag/${tag.slug}`}
                className="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              >
                {tag.title}
              </Link>
            ))}
          </div>

          {article.relatedPosts && article.relatedPosts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Posts</h2>
              <div className="grid gap-8 md:grid-cols-2">
                {article.relatedPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group block bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 mb-2">
                        {post.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </footer>
      </article>
    </>
  );
} 